// Story 6.9 (ADO-1163, AC4) — Select / Dropdown (NEW primitive).
//
// Built on a native <select> (styled) so keyboard + screen-reader behaviour is
// correct for free; a decorative caret overlays it. Used by the Admin Create/Edit
// forms (role, depot, account type). a11y: explicit <label htmlFor>.

import { useId, useState } from 'react';
import type { ChangeEvent, SelectHTMLAttributes } from 'react';
import { Icon } from '../Icon/Icon';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  options: SelectOption[];
  /** Placeholder shown as a disabled first option when the value is empty. */
  placeholder?: string;
  id?: string;
}

export function Select({
  label,
  options,
  placeholder = 'Select',
  id,
  value,
  defaultValue,
  onChange,
  className,
  ...rest
}: SelectProps) {
  const reactId = useId();
  const fieldId = id ?? reactId;

  // Track the current value so the placeholder-grey follows the real selection in
  // BOTH controlled (`value`) and uncontrolled (`defaultValue`) usage. Without this,
  // an uncontrolled Select leaves `value === undefined` forever and stays grey even
  // after the user picks a real option.
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(() =>
    defaultValue == null ? '' : String(defaultValue),
  );
  const currentValue = isControlled ? value : uncontrolledValue;
  const showingPlaceholder = currentValue === '' || currentValue === undefined;

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!isControlled) setUncontrolledValue(event.target.value);
    onChange?.(event);
  };

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={fieldId}>
        {label}
      </label>
      <div className={styles.control}>
        {/* `data-placeholder` (not a class) drives the placeholder-grey so the state
            is observable in the DOM and testable; CSS targets it via an attribute
            selector consuming only tokens. */}
        <select
          id={fieldId}
          className={[styles.select, className].filter(Boolean).join(' ')}
          data-placeholder={showingPlaceholder}
          onChange={handleChange}
          {...(isControlled ? { value } : { defaultValue })}
          {...rest}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <Icon name="caretDown" size={18} className={styles.caret} />
      </div>
    </div>
  );
}
